import * as angular from 'angular';
import * as $ from 'jquery';
import { debounce } from 'lodash';
import { dispatchEvent } from '@mode/shared/util-js';
import { AngularEventCallback } from '~/common/types';

const MIN_TILE_SIZE = 75; // minimum size of tile before switching to minimized version
const RESET_TILE_SIZE = -1; // uninitialized tile size

class TiledPanesController implements angular.IController {
  static $inject = ['$window', '$element', '$timeout'];

  private hostResizeObserver?: ResizeObserver;
  private windowResizeHandler?: (event?: UIEvent) => void;

  primaryTheme: 'dark' | '';
  vertical: boolean;
  showPrimary: boolean;
  showSecondary: boolean;
  adjustable: boolean;
  hide: boolean;
  containerHeight: number; // height of the tiled-panes element
  primaryHeight: number;
  secondaryHeight: number;
  resizeOnDragEnd = true;

  onPrimaryMaximized: AngularEventCallback<Event>;
  onPrimaryMinimized: AngularEventCallback<Event>;
  onSecondaryMaximized: AngularEventCallback<Event>;
  onSecondaryMinimized: AngularEventCallback<Event>;

  handle: JQuery;
  yTop: number; // offset from the top of the window
  primaryEl: JQuery;
  secondaryEl: JQuery;
  overlay: JQuery;

  // store the heights during resize for recall
  heights = {
    top: RESET_TILE_SIZE,
    bottom: RESET_TILE_SIZE,
    left: RESET_TILE_SIZE,
    right: RESET_TILE_SIZE,
  };

  constructor(private $window: Window, private $elem: JQuery, private $timeout: angular.ITimeoutService) {}

  private dispatchResizeEvent() {
    this.$timeout(() => dispatchEvent(this.$window, 'resize'));
  }

  updateHandle() {
    if (this.vertical) {
      this.handle.css({
        left: '',
        right: '',
        bottom: '',
        top: this.heights.top !== RESET_TILE_SIZE ? `calc(${this.heights.top}% - 4px)` : '',
      });
    } else {
      this.handle.css({
        left: this.heights.left !== RESET_TILE_SIZE ? `calc(${this.heights.left}% - 4px)` : '',
        right: '',
        bottom: '',
        top: '',
      });
    }
  }

  resetPanes() {
    this.primaryEl.removeAttr('style');
    this.secondaryEl.removeAttr('style');

    if (
      this.vertical &&
      this.heights.top !== RESET_TILE_SIZE &&
      !this.isPrimaryMaximized &&
      !this.isSecondaryMaximized
    ) {
      this.primaryEl.css({
        height: `${this.heights.top}%`,
      });
      this.secondaryEl.css({
        height: `${this.heights.bottom}%`,
        top: `${this.heights.top}%`,
      });
    } else if (
      !this.vertical &&
      this.heights.left !== RESET_TILE_SIZE &&
      !this.isPrimaryMaximized &&
      !this.isSecondaryMaximized
    ) {
      this.primaryEl.css({
        width: `${this.heights.left}%`,
      });
      this.secondaryEl.css({
        width: `${this.heights.right}%`,
        left: `${this.heights.left}%`,
      });
    }
  }

  toVert(num, navHeight) {
    return ((num - navHeight) / this.containerHeight) * 100;
  }

  toHoriz(num, offset, secondOffset) {
    const off = offset || 0;
    const secondOff = secondOffset || 0;
    return ((num - off) / (this.$window.innerWidth - off - secondOff)) * 100;
  }

  startResize(ev: JQueryMouseEventObject) {
    const $window = $(this.$window);
    this.containerHeight = this.$elem.height();
    this.yTop = this.$elem.offset().top;
    this.overlay.show();
    this.handle.addClass('adjusting');
    this.$window.document.documentElement.style.cursor = this.handle.css('cursor');

    $window.on('mouseup.resize', this.endResize.bind(this));
    $window.on('mousemove.resize', this.handleResize.bind(this));
  }

  endResize(ev: JQueryMouseEventObject) {
    const $window = $(this.$window);
    $window.off('mouseup.resize');
    $window.off('mousemove.resize');
    this.handle.removeClass('adjusting');
    this.$window.document.documentElement.style.cursor = '';

    this.overlay.hide();

    // When making one panel not able to be minimized by not implementing the methods, the resize
    // event causes the handle to readjust to the middle. This is optional since some implementations
    // depend on this resize event to force contents to resize.
    if (this.resizeOnDragEnd) {
      this.dispatchResizeEvent(); // for visualizations to resize
    }
  }

  handleResize(ev: JQueryMouseEventObject) {
    if (this.vertical) {
      const topHeight = ev.pageY - this.yTop;
      const topPercent = (topHeight / this.containerHeight) * 100;

      const botPercent = 100 - topPercent;
      const botHeight = this.containerHeight - topHeight;

      if (topHeight < MIN_TILE_SIZE) {
        if (this.showPrimary) {
          this.onPrimaryMinimized({ $event: ev });
        }

        this.heights.top = this.heights.bottom = RESET_TILE_SIZE;
      } else if (botHeight < MIN_TILE_SIZE) {
        if (this.showSecondary) {
          this.onSecondaryMinimized({ $event: ev });
        }

        this.heights.top = this.heights.bottom = RESET_TILE_SIZE;
      } else {
        if (!this.showPrimary) {
          this.onPrimaryMinimized({ $event: ev });
        }

        if (!this.showSecondary) {
          this.onSecondaryMinimized({ $event: ev });
        }

        this.primaryEl.css({
          height: `${topPercent}%`,
        });
        this.secondaryEl.css({
          height: `${botPercent}%`,
          top: `${topPercent}%`,
        });

        this.handle.css('top', `calc(${topPercent}% - 4px)`);
        this.heights.top = topPercent;
        this.heights.bottom = botPercent;
      }
    } else {
      const offset = this.$elem[0].getBoundingClientRect();
      const right = this.$window.innerWidth - offset.right;
      const leftPercent = this.toHoriz(ev.pageX, offset.left, right);
      const leftHeight = ev.pageX - offset.left;
      const rightPercent = this.toHoriz(this.$window.innerWidth - ev.pageX - right + offset.left, offset.left, right);
      const rightHeight = this.$window.innerWidth - ev.pageX - right;

      if (leftHeight < MIN_TILE_SIZE) {
        if (this.showPrimary) {
          this.onPrimaryMinimized({ $event: ev });
        }

        this.heights.left = this.heights.right = RESET_TILE_SIZE;
      } else if (rightHeight < MIN_TILE_SIZE) {
        if (this.showSecondary) {
          this.onSecondaryMinimized({ $event: ev });
        }

        this.heights.left = this.heights.right = RESET_TILE_SIZE;
      } else {
        if (!this.showPrimary) {
          this.onPrimaryMinimized({ $event: ev });
        }

        if (!this.showSecondary) {
          this.onSecondaryMinimized({ $event: ev });
        }

        this.primaryEl.css({
          width: `${leftPercent}%`,
        });
        this.secondaryEl.css({
          width: `${rightPercent}%`,
          left: `${leftPercent}%`,
        });

        this.handle.css('left', `calc(${leftPercent}% - 4px)`);
        this.heights.left = leftPercent;
        this.heights.right = rightPercent;
      }
    }
  }

  $postLink() {
    this.primaryEl = this.$elem.find('.primary-pane');
    this.secondaryEl = this.$elem.find('.secondary-pane');
    this.handle = this.$elem.find('.resize-handle');
    this.overlay = this.$elem.find('.resize-overlay');
    this.$elem.css('opacity', this.hide ? 0 : 1);

    this.sizePanels();
    this.updateHandle();

    this.$window.addEventListener('resize', this.onResize);

    this.handle.on('mousedown.resize', this.startResize.bind(this));

    this.dispatchResizeEvent();
    this.bindHostResizeObserver();
  }

  onResize = () => {
    this.updateHandle();
  };

  bindHostResizeObserver() {
    if (!('ResizeObserver' in this.$window)) return;
    let hostResizeHandler: ReturnType<typeof debounce>;

    /**
     * Cancel pending window resize dispatches if we're seeing a resize event.
     * Without this we would double dispatch on native events:
     *
     * 1. User resizes window
     * 2. Trusted/native resize event is dispatched
     * 2. Host element's dimensions change as a result
     * 3. ResizeObserver dispatches resize event
     *
     * This prevents that
     */
    this.$window.addEventListener(
      'resize',
      (this.windowResizeHandler = () => {
        hostResizeHandler.cancel();
      }),
      {
        passive: true,
        capture: true,
      }
    );

    /**
     * Dispatches a window resize event whenever the host element's
     * dimensions change
     */
    hostResizeHandler = debounce(
      () => {
        this.dispatchResizeEvent();
      },
      100,
      {
        leading: false,
        trailing: true,
      }
    );

    this.hostResizeObserver = new ResizeObserver(hostResizeHandler);
    this.hostResizeObserver.observe(this.$elem.get(0));
  }

  $onDestroy() {
    if (this.hostResizeObserver) {
      this.$window.removeEventListener('resize', this.windowResizeHandler, true);
      this.hostResizeObserver.disconnect();
      this.windowResizeHandler = null;
      this.hostResizeObserver = null;
    }

    this.$window.removeEventListener('resize', this.onResize);
  }

  $onChanges(changed) {
    if (changed.showSecondary || changed.showPrimary || changed.vertical) {
      if (this.handle) {
        this.updateHandle();
        this.resetPanes();
        this.dispatchResizeEvent();
      }
    }

    if (changed.hide) {
      this.$elem.css('opacity', this.hide ? 0 : 1);
    }
  }

  primaryHeaderToggle($event) {
    if (!this.showPrimary) {
      this.onPrimaryMaximized({ $event });
    } else {
      this.onPrimaryMaximized({ $event });
    }
  }

  secondaryHeaderToggle($event) {
    if (!this.showSecondary) {
      this.onSecondaryMinimized({ $event });
    } else {
      this.onSecondaryMaximized({ $event });
    }
  }

  minSecondaryMinimize($event: MouseEvent) {
    // stop propagation to prevent secondaryHeaderToggle from firing on top of this
    $event.preventDefault();
    $event.stopPropagation();
    this.onSecondaryMinimized({ $event });
  }

  minSecondaryMaximize($event: MouseEvent) {
    // stop propagation to prevent secondaryHeaderToggle from firing on top of this
    $event.preventDefault();
    $event.stopPropagation();
    this.onSecondaryMaximized({ $event });
  }

  get handleClasses() {
    const classes = ['handle-bar', this.vertical ? 'vertical' : 'horizontal'];
    if (this.vertical) {
      if (this.isPrimaryMaximized) {
        classes.push('top');
      } else if (this.isSecondaryMaximized) {
        classes.push('bottom');
      }
    } else {
      if (this.isPrimaryMaximized) {
        classes.push('left');
      } else if (this.isSecondaryMaximized) {
        classes.push('right');
      }
    }

    return classes.join(' ');
  }

  get simpleHandleClasses() {
    const classes = [this.vertical ? 'horiz' : 'vert'];
    return classes.join(' ');
  }

  get isPrimaryMaximized() {
    return this.showPrimary && !this.showSecondary;
  }

  get isSecondaryMaximized() {
    return !this.showPrimary && this.showSecondary;
  }

  sizePanels() {
    if (this.secondaryHeight && this.primaryHeight && this.showSecondary && !this.isSecondaryMaximized) {
      this.heights.top = this.primaryHeight;
      this.heights.bottom = this.secondaryHeight;
      this.primaryEl.css({
        height: `${this.heights.top}%`,
      });
      this.secondaryEl.css({
        height: `${this.heights.bottom}%`,
        top: `${100 - this.heights.bottom}%`,
      });
    }
  }
}

angular.module('mode.components').directive('tiledPanes', [
  function () {
    return {
      scope: {
        adjustable: '<',
        isFullHeight: '<',
        disableResizeOptions: '<',
        hide: '<',
        hidePrimaryHeader: '@',
        hideSecondaryHeader: '<',
        onPrimaryMaximized: '&',
        onPrimaryMinimized: '&',
        onSecondaryMaximized: '&',
        onSecondaryMinimized: '&',
        primaryHeight: '<',
        primaryTheme: '<',
        resizeOnDragEnd: '<?',
        secondaryCollapsedTitle: '@',
        secondaryHeight: '<',
        showPrimary: '<',
        showSecondary: '<',
        vertical: '<',
      },
      controller: TiledPanesController,
      controllerAs: 'ctrl',
      bindToController: true,
      transclude: {
        primary: 'primaryPane',
        primaryHeader: 'primaryHeader',
        secondary: 'secondaryPane',
        secondaryHeader: 'secondaryHeader',
        postPrimaryResizeActions: '?postPrimaryResizeActions',
        postSecondaryResizeActions: '?postSecondaryResizeActions',
      },
      template: `<div class="primary-pane {{ ctrl.primaryTheme }}" ng-class="{only: ctrl.isPrimaryMaximized, 'left-window': !ctrl.vertical, 'top-window': ctrl.vertical, min: !ctrl.showPrimary}">
        <div class="primary-header" ng-dblclick="ctrl.primaryHeaderToggle($event)" ng-if="!ctrl.hidePrimaryHeader" data-test="tiled-panes-primary-header">
          <ul class="editor-resize-actions" ng-class="{dark: ctrl.primaryTheme === 'dark'}">
            <li>
              <div class="additional-actions" ng-transclude="postPrimaryResizeActions"></div>
            </li>
          </ul>
          <div ng-transclude="primaryHeader"></div>
        </div>
        <div class="primary-pane-main" ng-transclude="primary" ng-show="ctrl.showPrimary"></div>
      </div>
      <div class="resize-handle" ng-class="ctrl.handleClasses" ng-show="ctrl.showPrimary && ctrl.showSecondary" data-test="tiled-panes-resize-handle">
        <div class="simple-handle" ng-class="ctrl.simpleHandleClasses"></div>
      </div>
      <div class="secondary-pane" ng-class="{only: ctrl.isSecondaryMaximized, 'right-window': !ctrl.vertical, 'bottom-window': ctrl.vertical, min: !ctrl.showSecondary}">
      <div class="secondary-header" ng-if="ctrl.showSecondary && !ctrl.hideSecondaryHeader" ng-dblclick="ctrl.secondaryHeaderToggle($event)">
          <ul class="editor-resize-actions" ng-if="!ctrl.disableResizeOptions">
            <li><a href="#" ng-click="ctrl.onSecondaryMinimized({ $event: $event })" title="Minimize panel"><i class="icon-panel-minimize-default align-md-icon"></i></a></li>
            <li><a href="#" ng-click="ctrl.onSecondaryMaximized({ $event: $event })" title="Maximize panel"><i class="icon-panel-maximize-default align-md-icon"></i></a></li>
            <li><div class="additional-actions" ng-transclude="postSecondaryResizeActions"></div></li>
          </ul>
          <div  ng-transclude="secondaryHeader"></div>
        </div>
        <div ng-transclude="secondary" ng-show="ctrl.showSecondary" ng-class="{'pane-content': true, 'data-overview': ctrl.isFullHeight}"></div>
        <div class="two-minimized {{ ctrl.primaryTheme }}" ng-show="!ctrl.showSecondary" ng-click="ctrl.secondaryHeaderToggle($event)">
          <h3 class="editor-nav-title collapsed" data-test="footer-title">{{ ctrl.secondaryCollapsedTitle }}</h3>
          <ul class="editor-resize-actions" ng-if="!ctrl.disableResizeOptions">
            <li><a href="#" ng-click="ctrl.minSecondaryMinimize($event)" title="Minimize panel"><i class="icon-panel-minimize-default align-md-icon"></i></a></li>
            <li><a href="#" ng-click="ctrl.minSecondaryMaximize($event)" title="Maximize panel"><i class="icon-panel-maximize-default align-md-icon"></i></a></li>
            <li><div class="additional-actions" ng-transclude="postSecondaryResizeActions"></div></li>
          </ul>
        </div>
      </div>
      <div class="resize-overlay"></div>
      `,
    };
  },
]);
